import { Download } from 'lucide-react';

import { LEGAL_DOCUMENTS } from './documents.config';
import { usePdfAvailability } from './use-pdf-availability';

import { Button } from '@/components/ui/button';

/** Static asset path of the signed DoC PDF (see tools/build-legal-docs.mjs). */
export function docPdfUrl(): string {
    return `/legal/doc/${LEGAL_DOCUMENTS.doc.file}`;
}

/**
 * EU Declaration of Conformity page (ESCLOUD-77 / ESCLOUD-181): renders the
 * current signed DoC PDF and identifies the device version it covers.
 */
export function DeclarationOfConformityPage() {
    const config = LEGAL_DOCUMENTS.doc;
    const pdfUrl = docPdfUrl();
    const { data: pdfAvailable } = usePdfAvailability(pdfUrl);

    return (
        <div className="flex h-full min-h-[70vh] w-full flex-col gap-4 py-6">
            <header className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold">EU Declaration of Conformity</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Version {config.version} — effective {config.effectiveDate} — covers ESCloud version{' '}
                        {config.deviceVersion}
                    </p>
                </div>
                {pdfAvailable === false ? (
                    // `disabled` has no effect on an asChild anchor — render a
                    // plain disabled button while the PDF is unavailable.
                    <Button variant="outline" size="sm" disabled>
                        <Download /> Download PDF
                    </Button>
                ) : (
                    <Button variant="outline" size="sm" asChild>
                        <a href={pdfUrl} download={config.file}>
                            <Download /> Download PDF
                        </a>
                    </Button>
                )}
            </header>

            {pdfAvailable === false ? (
                // Probe failed: show a readable notice rather than a raw browser
                // error pane inside the iframe.
                <div className="flex min-h-[60vh] w-full flex-1 items-center justify-center rounded-md border bg-muted/30 p-6">
                    <p className="max-w-md text-center text-sm text-muted-foreground">
                        The Declaration of Conformity could not be loaded. Please try again later, or
                        request a copy from{' '}
                        <a className="underline" href="mailto:contact@therenva.com">
                            contact@therenva.com
                        </a>
                        .
                    </p>
                </div>
            ) : (
                <iframe
                    src={pdfUrl}
                    title="EU Declaration of Conformity"
                    className="min-h-[60vh] w-full flex-1 rounded-md border bg-muted/30"
                />
            )}
        </div>
    );
}
